import { Component, Inject, OnDestroy } from '@angular/core';
import { DocumentNode, ApproverNode, User, ProcedureDocument, DocumentApproval } from '@completion/models';
import { Store } from '@ngrx/store';
import { Subject, Observable, of } from 'rxjs';
import { selectEditMode, getCurrentDocument, getCurrentUser, getProjectEditorsAndAdmins } from '@completion/selectors';
import { FormControl } from '@angular/forms';
import { DocumentUpdateQueueService } from '../../../../core/services/document-update-queue.service';
import { MatDialog } from '@angular/material/dialog';
import { UserSelectDialogComponent, UserSelectDialogData } from '../../../../shared/user-select/user-select-dialog.component';
import { persistNodeUpdateForNode } from '../node-persistence.util';
import { takeUntil, take } from 'rxjs/operators';
import { DocumentUpdate } from '@completion/actions';
import { ConfirmationDialogComponent } from '../../../../shared/confirmation-dialog';
import { collectAssignedUserIds, filterUnassignedCandidates } from '@completion/utils';
import { RemoveUserDialogComponent } from '../../../../shared/remove-user-dialog/remove-user-dialog.component';

@Component({
  selector: 'app-approver',
  templateUrl: './approver.component.html',
  styleUrls: ['./approver.component.scss'],
  standalone: false,
})
export class ApproverComponent implements OnDestroy {
  documentNode: DocumentNode;
  editMode = false;
  currentUser: User = null as any;

  control = new FormControl('');
  private readonly destroy$ = new Subject();
  users$: Observable<User[]> = of([]);
  private currentDocument: ProcedureDocument | null = null;

  constructor(
    @Inject('documentNode') documentNode: DocumentNode,
    private store: Store,
    private updateQueue: DocumentUpdateQueueService,
    private dialog: MatDialog,
  ) {
    this.documentNode = documentNode;

    this.store
      .select(selectEditMode)
      .pipe(takeUntil(this.destroy$))
      .subscribe((m) => (this.editMode = m));

    this.store
      .select(getCurrentUser)
      .pipe(takeUntil(this.destroy$))
      .subscribe((u) => (this.currentUser = u as User));

    this.store
      .select(getCurrentDocument)
      .pipe(takeUntil(this.destroy$))
      .subscribe((doc) => {
        this.currentDocument = doc as ProcedureDocument;
      });

    try {
      this.users$ = this.store.select(getProjectEditorsAndAdmins);
    } catch {
      this.users$ = of([]);
    }
  }

  get approver(): ApproverNode | null {
    return (this.documentNode as ApproverNode) || null;
  }

  get isAssignedCurrentUser(): boolean {
    const node = this.documentNode as ApproverNode;
    const assignedId = node && node.user ? node.user.id || null : null;
    const currentId = this.currentUser ? this.currentUser.id || null : null;
    return assignedId != null && currentId != null && String(assignedId) === String(currentId);
  }

  get hasApproved(): boolean {
    return !!this.approvalDate;
  }

  get approvalDate(): string | null {
    const approverUser = (this.documentNode as ApproverNode)?.user;
    const approvals = this.currentDocument?.approvals || [];
    if (!approverUser || !approvals.length) {
      return null;
    }
    const approverId = approverUser.id;
    if (approverId == null) {
      return null;
    }
    const match = approvals.find(
      (a) => a && a.approver && a.approver.id != null && String(a.approver.id) === String(approverId) && !!a.approvedTimestamp,
    );
    return match ? match.approvedTimestamp : null;
  }

  get canApprove(): boolean {
    // Only the assigned approver can approve and only if not already approved
    return this.isAssignedCurrentUser && !this.hasApproved;
  }

  selectUser(user: User) {
    if (this.isAssignedCurrentUser) {
      this.control.setValue('');
      return;
    }

    const node = this.documentNode as ApproverNode;
    const newContent = user?.name || '';
    this.control.setValue('');

    // Use shared helper which clones the current document, mutates the node and enqueues update
    persistNodeUpdateForNode(this.store, this.updateQueue, node, (n: any) => {
      n.user = user;
      n.content = newContent;
    });
  }

  editApprover() {
    if (this.isAssignedCurrentUser) {
      return;
    }
    const preselectedId = this.approver?.user?.id ?? undefined;
    this.store
      .select(getCurrentDocument)
      .pipe(take(1))
      .subscribe((doc) => {
        const pending = (this.updateQueue as any).getLastQueued ? (this.updateQueue as any).getLastQueued() : null;
        const effectiveDoc = pending || doc;
        const assignedIds = collectAssignedUserIds(effectiveDoc);
        const excludeSet = new Set<number>(assignedIds.filter((id) => id !== (preselectedId == null ? undefined : Number(preselectedId))));
        const myId = this.currentUser && (this.currentUser as any).id != null ? Number((this.currentUser as any).id) : undefined;
        if (myId != null && myId !== (preselectedId == null ? undefined : Number(preselectedId))) {
          excludeSet.add(myId);
        }
        const excludeIds = Array.from(excludeSet);

        this.users$.pipe(take(1)).subscribe((list) => {
          const filtered = filterUnassignedCandidates(list || [], effectiveDoc);
          const final = filtered.filter((uitem) => {
            const uid = (uitem as any).id;
            if (preselectedId != null && String(uid) === String(preselectedId)) return true;
            if (myId != null && String(uid) === String(myId)) return false;
            return true;
          });

          const data: UserSelectDialogData = {
            title: 'Select approver',
            users: final,
            preselectedUserId: preselectedId,
            excludeUserIds: excludeIds,
          };
          const ref = this.dialog.open(UserSelectDialogComponent, { width: '420px', data });
          ref
            .afterClosed()
            .pipe(take(1))
            .subscribe((user: User | undefined) => {
              if (user) {
                this.selectUser(user);
              }
            });
        });
      });
  }

  onApproveClick() {
    if (!this.canApprove) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: 'Approve procedure?',
        message: 'Do you want to approve this procedure? This will be recorded as your approval and can not be undone.',
        confirmText: 'Approve',
        cancelText: 'Cancel',
        isCancelDisplay: true,
        confirmationTitle: 'Approve',
        severity: 'warn',
        confirmButtonSeverity: 'accept',
        icon: 'gavel',
      },
    });

    dialogRef
      .afterClosed()
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe((result) => {
        if (!result) {
          return;
        }

        this.store
          .select(getCurrentDocument)
          .pipe(take(1))
          .subscribe((doc: ProcedureDocument | null | undefined) => {
            if (!doc) {
              return;
            }

            // Deep-clone the document to avoid mutating state directly
            const cloned = JSON.parse(JSON.stringify(doc)) as ProcedureDocument;
            if (!Array.isArray(cloned.approvals)) {
              cloned.approvals = [];
            }

            const approval: DocumentApproval = {
              // backend will assign real id
              approver: this.currentUser,
              approvedTimestamp: new Date().toISOString(),
            };

            cloned.approvals.push(approval);

            try {
              this.store.dispatch(new DocumentUpdate(cloned));
            } catch {
              // if class-based action fails for some reason, swallow to avoid breaking UI
            }
          });
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  removeApprover() {
    const ref = this.dialog.open(RemoveUserDialogComponent, {
      width: '500px',
      data: { title: 'Remove approver', message: 'Choose whether to unassign the user from this approver node or delete the entire node.' },
      panelClass: 'remove-user-panel',
    });
    ref
      .afterClosed()
      .pipe(take(1))
      .subscribe((res: any) => {
        if (!res) return;
        if (res === 'unassign') {
          const node = this.documentNode as ApproverNode;
          persistNodeUpdateForNode(this.store, this.updateQueue, node, (n: any) => {
            n.user = null;
            n.content = '';
          });
          return;
        }
        if (res === 'delete') {
          this.store
            .select(getCurrentDocument)
            .pipe(take(1))
            .subscribe((doc) => {
              if (!doc) return;
              const cloned = JSON.parse(JSON.stringify(doc));
              const removeNodeByIdImmutable = (nodes: any[], id: number): { nodes: any[]; removed: boolean } => {
                if (!Array.isArray(nodes) || nodes.length === 0) return { nodes, removed: false };
                let removed = false;
                const next: any[] = [];
                for (const n of nodes) {
                  if (!n) continue;
                  if (n.id === id) {
                    removed = true;
                    continue;
                  }
                  let updatedChildren = n.children;
                  if (Array.isArray(n.children) && n.children.length) {
                    const res = removeNodeByIdImmutable(n.children, id);
                    if (res.removed) {
                      updatedChildren = res.nodes;
                      removed = true;
                    }
                  }
                  next.push(updatedChildren !== n.children ? { ...n, children: updatedChildren } : n);
                }
                return removed ? { nodes: next, removed: true } : { nodes, removed: false };
              };
              const { nodes: updatedNodes, removed } = removeNodeByIdImmutable(cloned.nodes || [], (this.documentNode as any).id);
              if (!removed) return;
              cloned.nodes = updatedNodes;
              try {
                // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                (this.updateQueue as any).assignOrdinals ? (this.updateQueue as any).assignOrdinals(cloned) : null;
              } catch {}
              if ((this.updateQueue as any).queueImmediate) {
                (this.updateQueue as any).queueImmediate(cloned);
              } else {
                this.updateQueue.queue(cloned);
              }
            });
        }
      });
  }
}
